import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { detailSectionsGapClassName } from "@/components/ui/detail-box";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type EmbeddedPageSkeletonProps = {
  /** Taller top block (e.g. tracking summary with finance). */
  tallSummary?: boolean;
  className?: string;
};

/**
 * Single content-area skeleton under already-mounted chrome.
 * Shells and section pages share this shape so handoffs do not flash a second design.
 */
export function EmbeddedPageSkeleton({
  tallSummary = false,
  className,
}: EmbeddedPageSkeletonProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div
      className={cn("flex w-full flex-col", detailSectionsGapClassName, className)}
      aria-busy="true"
      role="status"
    >
      <span className="sr-only">{t("common.loading")}</span>
      <Skeleton className={cn("w-full rounded-xl", tallSummary ? "h-56" : "h-36")} />
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-10 w-full max-w-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}
